import { ScrollArea, Stack, Table, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import UserDetails from '@/components/Details/UserDetails/UserDetails'
import UserVotesModal from '@/components/Modals/Governance/UserVotesModal'
import classes from '@/components/Tables/WithdrawTable.module.css'
import { CurrentVote, fetchAdminVotes } from '@/hooks/useElectionsServer'
import useSpacesAuthAdmin from '@/hooks/useSpacesAuthAdmin'
import { processBalance } from '@/utils/utils'

import RootButton from '../Buttons/RootButton'

const AdminVotes: React.FC = () => {
  const { t } = useTranslation()
  const { token } = useSpacesAuthAdmin()
  const [votes, setVotes] = useState<CurrentVote[]>([])
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [opened, { open, close }] = useDisclosure(false)

  useEffect(() => {
    const getVotes = async () => {
      if (!token) return
      try {
        const currentVotes = await fetchAdminVotes(token)
        setVotes(currentVotes)
      } catch (error) {
        console.error('Failed to fetch admin votes:', error)
      }
    }
    getVotes()
  }, [token])

  const stats = useMemo(() => {
    const uniqueUsers = new Set(votes.map(v => v.user)).size
    const totalVotes = votes.length
    const totalAmount = votes.reduce((acc, v) => acc + BigInt(v.amount), BigInt(0))

    // Calculate per-user totals
    const userTotals = votes.reduce(
      (acc, vote) => {
        if (!acc[vote.user]) {
          acc[vote.user] = BigInt(0)
        }
        acc[vote.user] += BigInt(vote.amount)
        return acc
      },
      {} as Record<string, bigint>
    )

    // Calculate per-user unique candidates
    const userUniqueCandidates = votes.reduce(
      (acc, vote) => {
        if (!acc[vote.user]) {
          acc[vote.user] = new Set()
        }
        acc[vote.user].add(vote.candidate)
        return acc
      },
      {} as Record<string, Set<string>>
    )

    return {
      uniqueUsers,
      totalVotes,
      totalAmount: processBalance(totalAmount),
      userTotals: Object.entries(userTotals)
        .map(([user, amount]) => ({
          user,
          amount: processBalance(amount),
          candidates: userUniqueCandidates[user].size,
        }))
        .sort((a, b) => Number(b.amount.value - a.amount.value)),
    }
  }, [votes])

  return (
    <Stack style={{ gap: '15px', height: '100%' }}>
      <Stack style={{ gap: '10px' }}>
        <Title order={4}>
          {t('components.elections.admin.votes.totalVotes', 'Total Votes Cast')}: {stats.totalVotes}
        </Title>
        <Title order={4}>
          {t('components.elections.admin.votes.uniqueUsers', 'Unique Users')}: {stats.uniqueUsers}
        </Title>
        <Title order={4}>
          {t('components.elections.admin.votes.totalAmount', 'Total Amount')}:{' '}
          {stats.totalAmount.display} VOTE
        </Title>
      </Stack>

      <Title order={4}>{t('components.elections.admin.votes.perUser', 'Votes Per User')}</Title>
      <Table verticalSpacing={2} stickyHeader>
        <ScrollArea h={600}>
          <Table.Thead className={classes.tableHeader}>
            <Table.Tr>
              <Table.Th className={classes.tableHeader}>User</Table.Th>
              <Table.Th className={classes.tableHeader}>Amount</Table.Th>
              <Table.Th className={classes.tableHeader}>Candidates</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {stats.userTotals.map(({ user, amount, candidates }) => (
              <Table.Tr key={user}>
                <Table.Td className={classes.tableRow}>
                  <UserDetails userId={user} width="fit-content" />
                </Table.Td>
                <Table.Td className={classes.tableRow}>{amount.display}</Table.Td>
                <Table.Td className={classes.tableRow}>{candidates}</Table.Td>
                <Table.Td className={classes.tableRow}>
                  <RootButton
                    style1={false}
                    expand
                    onClick={() => {
                      setSelectedUser(user)
                      open()
                    }}
                  >
                    {t('buttons.viewMore', 'View More')}
                  </RootButton>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </ScrollArea>
      </Table>

      {selectedUser && (
        <UserVotesModal
          opened={opened}
          onClose={() => {
            close()
            setSelectedUser(null)
          }}
          votes={votes}
          userId={selectedUser}
        />
      )}
    </Stack>
  )
}

export default AdminVotes
